import React from 'react';
import { TrendingDown } from 'lucide-react';
import { formatQuantity as formatNumber } from '@/utils/formatters';

/**
 * NettStatusTable - Displays item nett status in a compact 8-column grid format.
 * 
 * Used by:
 * - ProductionFulfillmentPage (shortageLabel="production")
 * - PurchaseFulfillmentPage (shortageLabel="purchase")
 * 
 * @param {Object} props
 * @param {Object} props.nettStatus - Object containing stock status fields
 * @param {number} props.nettStatus.stock_in_hand - Current stock
 * @param {number} props.nettStatus.wip - Work in Progress
 * @param {number} props.nettStatus.ohwip - On-Hold WIP
 * @param {number} props.nettStatus.indent_qty - Indent quantity
 * @param {number} props.nettStatus.po_issued - PO issued quantity
 * @param {number} props.nettStatus.blocked - Blocked quantity
 * @param {number} props.nettStatus.so_blocked - SO blocked quantity
 * @param {number} props.nettStatus.nett - Nett available quantity
 * @param {number} props.shortage - Shortage quantity (displays warning if > 0)
 * @param {string} [props.shortageLabel="production"] - Label for shortage message ("production" or "purchase")
 */
export const NettStatusTable = ({ nettStatus, shortage, shortageLabel = "production" }) => (
  <div className="space-y-2">
    <div className="grid grid-cols-8 gap-1 text-xs bg-slate-50 rounded-lg p-2">
      <div className="text-center">
        <div className="text-slate-500 font-medium">Stock</div>
        <div className="font-semibold text-slate-700">{formatNumber(nettStatus.stock_in_hand)}</div>
      </div>
      <div className="text-center">
        <div className="text-slate-500 font-medium">WIP</div>
        <div className="font-semibold text-slate-700">{formatNumber(nettStatus.wip)}</div>
      </div>
      <div className="text-center">
        <div className="text-slate-500 font-medium">OHWIP</div>
        <div className="font-semibold text-slate-700">{formatNumber(nettStatus.ohwip)}</div>
      </div>
      <div className="text-center">
        <div className="text-slate-500 font-medium">Indent</div>
        <div className="font-semibold text-slate-700">{formatNumber(nettStatus.indent_qty)}</div>
      </div>
      <div className="text-center">
        <div className="text-slate-500 font-medium">PO Iss</div>
        <div className="font-semibold text-slate-700">{formatNumber(nettStatus.po_issued)}</div>
      </div>
      <div className="text-center">
        <div className="text-slate-500 font-medium">Blocked</div>
        <div className="font-semibold text-slate-700">{formatNumber(nettStatus.blocked)}</div>
      </div>
      <div className="text-center">
        <div className="text-slate-500 font-medium">SO Blk</div>
        <div className="font-semibold text-orange-600">{formatNumber(nettStatus.so_blocked)}</div>
      </div>
      <div className="text-center">
        <div className="text-slate-500 font-medium">NETT</div>
        <div className={`font-bold ${nettStatus.nett < 0 ? 'text-red-600' : 'text-green-600'}`}>
          {formatNumber(nettStatus.nett)}
        </div>
      </div>
    </div>
    
    {/* Shortage highlight */}
    {shortage > 0 && (
      <div className="flex items-center justify-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
        <TrendingDown className="h-4 w-4 text-red-600" />
        <span className="text-sm font-medium text-red-700">
          Shortage: <span className="text-lg font-bold">{formatNumber(shortage)}</span> pcs needs {shortageLabel}
        </span>
      </div>
    )}
  </div>
);

export default NettStatusTable;
